import { type ReactElement, useMemo, useState } from 'react';
import { type AnalyticsExportJob, type AnalyticsExportReceived, type AnalyticsExportSchedule, type AnalyticsExportTemplate } from '@vello/api-client';
import { Badge, Button, EmptyState, ListTable, Notice, PageHeader, TabPanel, Tabs, type Column, Text, InlineRow, InlineStack, Stack, Ink } from '@vello/web-theme';
import { describeRequest, formatBytes, isOpenJob, jobFromLink, statusView } from '../../src/index';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { ExportDialog, type ExportTarget } from '../components/bi/ExportDialog';
import { ScheduleDialog } from '../components/bi/ScheduleDialog';
import { useCanExport, useExportJobs, useExportSchedules, useExportTemplates } from '../components/bi/bi-exports-store';
import { useBiText } from '../components/bi/bi-ui';

type Tab = 'mine' | 'received' | 'schedules' | 'templates';

/**
 * Análises › Exportações (D532; doc 20 §8 row E3): the asynchronous exports of the operator - the files being prepared and ready (with progress,
 * download, cancel, delete), the files other operators' schedules delivered to them, the schedules (a cadence at a local time, operator recipients) and
 * the export templates. A job runs in the worker; while any is queued or running this screen reads the list again every few seconds. The link in a
 * delivery e-mail opens this screen with `?job=<id>`: it only highlights a row the server lets the operator see (a link grants nothing). The download
 * goes through the API, which asks the grants again at that moment; a personal file is access-logged first.
 */
export function AnalyticsExportsScreen(): ReactElement {
  const { t, lang } = useT();
  const { v } = useVocab();
  const text = useBiText();
  const { confirm, toast } = useConsole();
  const canExport = useCanExport();
  const jobs = useExportJobs();
  const linked = useMemo(() => (typeof window !== 'undefined' ? jobFromLink(window.location.search) : undefined), []);
  const list = jobs.state.status === 'ready' ? jobs.state.value : undefined;
  const [picked, setPicked] = useState<Tab | undefined>(undefined);
  const linkedIsReceived = linked !== undefined && list?.received.some((r) => r.id === linked) === true && list.jobs.every((j) => j.id !== linked);
  const tab: Tab = picked ?? (linkedIsReceived ? 'received' : 'mine');
  const schedules = useExportSchedules(canExport);
  const templates = useExportTemplates(canExport);
  const [exporting, setExporting] = useState<ExportTarget | undefined>(undefined);
  const [scheduling, setScheduling] = useState<{ readonly schedule?: AnalyticsExportSchedule } | undefined>(undefined);
  const [downloading, setDownloading] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const when = new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' });
  const rowsFmt = new Intl.NumberFormat(lang);

  const download = async (id: string): Promise<void> => {
    setProblem(undefined);
    setDownloading(id);
    const res = await jobs.download(id);
    setDownloading(undefined);
    if (!res.ok) setProblem(t(res.error.messageId));
    else toast({ tone: 'success', message: t('bi.exp.downloaded', { name: res.value.fileName }) });
  };

  const jobName = (j: AnalyticsExportJob): string => {
    if (j.fileName !== undefined) return j.fileName;
    const d = describeRequest(j);
    return d.report !== undefined ? d.report : d.list !== undefined ? v('analytics.list_id', d.list) : d.metrics.map(text.metricName).join(', ') || v('analytics.export_source', j.source.kind);
  };
  const jobSummary = (j: AnalyticsExportJob): string => {
    const d = describeRequest(j);
    if (d.report !== undefined) return v('analytics.export_source', 'CATALOG_REPORT');
    if (d.list !== undefined) return [v('analytics.export_source', 'LIST_REPORT'), v('analytics.list_id', d.list)].join(' · ');
    const cuts = d.dimensions.map(text.dimName).join(', ');
    return [v('analytics.export_source', j.source.kind), d.metrics.map(text.metricName).join(', '), cuts].filter((x) => x !== '').join(' · ');
  };

  const askRemove = async (j: AnalyticsExportJob): Promise<void> => {
    const open = isOpenJob(j);
    const answer = await confirm(
      open
        ? { title: t('bi.exp.cancel.title'), body: t('bi.exp.cancel.body'), confirmLabel: t('bi.exp.cancel.confirm'), cancelLabel: t('bi.exp.cancel.keep'), tone: 'danger' }
        : { title: t('bi.exp.delete.title', { name: jobName(j) }), body: t('bi.exp.delete.body'), confirmLabel: t('bi.exp.delete.confirm'), cancelLabel: t('ui.cancel'), tone: 'danger' },
    );
    if (!answer.confirmed) return;
    const res = await jobs.remove(j.id);
    toast(res.ok ? { tone: 'success', message: t(open ? 'bi.exp.cancelled' : 'bi.exp.deleted') } : { tone: 'warning', message: t(res.error.messageId) });
  };

  const stamp = (iso: string | undefined): string => (iso === undefined ? '' : when.format(new Date(iso)));

  const jobColumns: readonly Column<AnalyticsExportJob>[] = [
    {
      key: 'file',
      header: t('bi.exp.col.file'),
      width: '1.6fr',
      render: (j) => (
        <InlineStack gap={1} {...(j.id === linked ? { 'aria-current': 'true' as const } : {})}>
          <Ink as="strong" breakAnywhere data-linked={j.id === linked ? 'true' : undefined}>{jobName(j)}</Ink>
          <Text role="caption" tone="secondary">{jobSummary(j)}</Text>
          <InlineRow gap={2} align="stretch" wrap>
            <Badge>{v('analytics.export_format', j.format)}</Badge>
            {j.scheduleId !== undefined && <Badge>{t('bi.exp.fromSchedule')}</Badge>}
            {j.personal && <Badge tone="warning">{t('bi.exp.personal')}</Badge>}
          </InlineRow>
        </InlineStack>
      ),
    },
    {
      key: 'status',
      header: t('bi.exp.col.status'),
      width: '1.1fr',
      render: (j) => {
        const view = statusView(j.status);
        const reason = j.status === 'failed' || j.status === 'cancelled' ? j.reasonCode : undefined;
        return (
          <InlineStack gap={1}>
            <span>
              <Badge tone={view.tone}>{v('analytics.export_status', j.status)}</Badge>
            </span>
            {j.status === 'running' && <Text role="caption" tone="secondary" mono aria-live="polite">{j.cancelRequested ? t('bi.exp.cancelRequested') : t('bi.exp.progress', { rows: rowsFmt.format(j.progressRows) })}</Text>}
            {reason !== undefined && <Text role="caption" tone="secondary">{v('analytics.export_reason', reason)}</Text>}
          </InlineStack>
        );
      },
    },
    {
      key: 'size',
      header: t('bi.exp.col.size'),
      width: '0.7fr',
      render: (j) => (j.status === 'done' ? <span className="tnum">{`${t('bi.exp.rows', { rows: rowsFmt.format(j.rowCount ?? 0) })} · ${formatBytes(j.byteSize)}`}</span> : ''),
    },
    { key: 'created', header: t('bi.exp.col.created'), width: '0.8fr', render: (j) => <span className="tnum">{stamp(j.createdAt)}</span> },
    { key: 'expires', header: t('bi.exp.col.expires'), width: '0.8fr', render: (j) => <span className="tnum">{j.status === 'done' ? stamp(j.expiresAt) : ''}</span> },
  ];

  const receivedColumns: readonly Column<AnalyticsExportReceived>[] = [
    {
      key: 'file',
      header: t('bi.exp.col.file'),
      width: '1.6fr',
      render: (r) => (
        <InlineStack gap={1} {...(r.id === linked ? { 'aria-current': 'true' as const } : {})}>
          <Ink as="strong" breakAnywhere data-linked={r.id === linked ? 'true' : undefined}>{r.fileName ?? v('analytics.export_source', r.source.kind)}</Ink>
          <InlineRow gap={2} align="stretch" wrap>
            <Badge>{v('analytics.export_format', r.format)}</Badge>
            {r.personal && <Badge tone="warning">{t('bi.exp.personal')}</Badge>}
          </InlineRow>
        </InlineStack>
      ),
    },
    { key: 'size', header: t('bi.exp.col.size'), width: '0.8fr', render: (r) => <span className="tnum">{`${t('bi.exp.rows', { rows: rowsFmt.format(r.rowCount ?? 0) })} · ${formatBytes(r.byteSize)}`}</span> },
    { key: 'finished', header: t('bi.exp.col.finished'), width: '0.8fr', render: (r) => <span className="tnum">{stamp(r.finishedAt)}</span> },
    { key: 'expires', header: t('bi.exp.col.expires'), width: '0.8fr', render: (r) => <span className="tnum">{stamp(r.expiresAt)}</span> },
  ];

  const cadenceText = (s: AnalyticsExportSchedule): string => {
    const c = s.cadence;
    const time = `${String(c.hour).padStart(2, '0')}:${String(c.minute).padStart(2, '0')}`;
    const words = c.kind === 'DAILY' ? t('bi.exp.cad.DAILY', { time }) : c.kind === 'WEEKLY' ? t('bi.exp.cad.WEEKLY', { weekday: t(`bi.exp.wd.${c.weekday}`), time }) : t('bi.exp.cad.MONTHLY', { day: c.day, time });
    return `${words} (${t('bi.exp.cad.zone', { zone: s.timezone })})`;
  };
  const nextText = (s: AnalyticsExportSchedule): string => {
    try {
      return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short', timeZone: s.timezone }).format(new Date(s.nextSlotAt));
    } catch {
      return stamp(s.nextSlotAt);
    }
  };

  const scheduleColumns: readonly Column<AnalyticsExportSchedule>[] = [
    {
      key: 'name',
      header: t('bi.exp.sch.col.name'),
      width: '1.2fr',
      render: (s) => (
        <InlineStack gap={1}>
          <strong>{s.name}</strong>
          <InlineRow gap={2} align="stretch" wrap>
            <Badge>{v('analytics.export_source', s.source.kind)}</Badge>
            <Badge>{v('analytics.export_format', s.format)}</Badge>
            {s.personal && <Badge tone="warning">{t('bi.exp.personal')}</Badge>}
            {!s.enabled && <Badge tone="warning">{t('bi.exp.sch.paused')}</Badge>}
          </InlineRow>
        </InlineStack>
      ),
    },
    { key: 'when', header: t('bi.exp.sch.col.when'), width: '1.2fr', render: (s) => cadenceText(s) },
    { key: 'next', header: t('bi.exp.sch.col.next'), width: '0.9fr', render: (s) => <span className="tnum">{s.enabled ? nextText(s) : ''}</span> },
    { key: 'recipients', header: t('bi.exp.sch.col.recipients'), width: '0.7fr', render: (s) => t('bi.exp.sch.recipientsCount', { count: s.recipients.length }) },
    {
      key: 'last',
      header: t('bi.exp.sch.col.last'),
      width: '1fr',
      render: (s) => {
        if (s.lastStatus === undefined) return <Text role="caption" tone="secondary">{t('bi.exp.sch.never')}</Text>;
        const reason = s.lastReasonCode;
        return (
          <InlineStack gap={1}>
            <span>
              <Badge tone={s.lastStatus === 'DONE' ? 'success' : s.lastStatus === 'FAILED' ? 'danger' : s.lastStatus === 'SKIPPED' ? 'warning' : 'neutral'}>{v('analytics.export_schedule_status', s.lastStatus)}</Badge>
            </span>
            {reason !== undefined && <Text role="caption" tone="secondary">{v('analytics.export_reason', reason)}</Text>}
          </InlineStack>
        );
      },
    },
  ];

  const templateColumns: readonly Column<AnalyticsExportTemplate>[] = [
    {
      key: 'name',
      header: t('bi.exp.tpl.col.name'),
      width: '1.2fr',
      render: (x) => (
        <InlineStack gap={1}>
          <strong>{x.name}</strong>
          <InlineRow gap={2} align="stretch" wrap>
            <Badge>{x.mine ? t('bi.exp.tpl.mine') : t('bi.exp.tpl.theirs')}</Badge>
            {x.shared && <Badge>{t('bi.exp.tpl.shared')}</Badge>}
            {x.personal && <Badge tone="warning">{t('bi.exp.personal')}</Badge>}
          </InlineRow>
        </InlineStack>
      ),
    },
    {
      key: 'what',
      header: t('bi.exp.tpl.col.what'),
      width: '1.4fr',
      render: (x) => {
        const d = describeRequest({ source: { kind: 'QUERY' }, request: x.request });
        return (
          <InlineStack gap={1}>
            <span>{d.metrics.map(text.metricName).join(', ')}</span>
            <Text role="caption" tone="secondary">{`${v('analytics.export_template_origin', x.origin)} · ${v('analytics.export_format', x.format)} · ${x.columns.length > 0 ? t('bi.exp.tpl.columns', { count: x.columns.length }) : t('bi.exp.tpl.allColumns')}`}</Text>
          </InlineStack>
        );
      },
    },
    { key: 'updated', header: t('bi.exp.tpl.col.updated'), width: '0.8fr', render: (x) => <span className="tnum">{stamp(x.updatedAt)}</span> },
  ];

  const askDeleteSchedule = async (s: AnalyticsExportSchedule): Promise<void> => {
    const answer = await confirm({ title: t('bi.exp.sch.delete.title', { name: s.name }), body: t('bi.exp.sch.delete.body'), confirmLabel: t('bi.exp.sch.delete'), cancelLabel: t('ui.cancel'), tone: 'danger' });
    if (!answer.confirmed) return;
    const res = await schedules.remove(s.id);
    toast(res.ok ? { tone: 'success', message: t('bi.exp.sch.deleted') } : { tone: 'warning', message: t(res.error.messageId) });
  };
  const askDeleteTemplate = async (x: AnalyticsExportTemplate): Promise<void> => {
    const answer = await confirm({ title: t('bi.exp.tpl.delete.title', { name: x.name }), body: t('bi.exp.tpl.delete.body'), confirmLabel: t('bi.exp.tpl.delete'), cancelLabel: t('ui.cancel'), tone: 'danger' });
    if (!answer.confirmed) return;
    const res = await templates.remove(x.id);
    toast(res.ok ? { tone: 'success', message: t('bi.exp.tpl.deleted') } : { tone: 'warning', message: t(res.error.messageId) });
  };
  const togglePause = async (s: AnalyticsExportSchedule): Promise<void> => {
    const res = await schedules.update(s.id, { enabled: !s.enabled });
    if (!res.ok) toast({ tone: 'warning', message: t(res.error.messageId) });
  };
  const toggleShare = async (x: AnalyticsExportTemplate): Promise<void> => {
    const res = await templates.update(x.id, { shared: !x.shared });
    if (!res.ok) toast({ tone: 'warning', message: t(res.error.messageId) });
  };

  const tabs = [
    { id: 'mine' as const, label: t('bi.exp.tab.mine'), count: list !== undefined ? String(list.jobs.length) : undefined },
    { id: 'received' as const, label: t('bi.exp.tab.received'), count: list !== undefined && list.received.length > 0 ? String(list.received.length) : undefined },
    ...(canExport
      ? [
          { id: 'schedules' as const, label: t('bi.exp.tab.schedules'), count: schedules.state.status === 'ready' ? String(schedules.state.value.length) : undefined },
          { id: 'templates' as const, label: t('bi.exp.tab.templates'), count: templates.state.status === 'ready' ? String(templates.state.value.length) : undefined },
        ]
      : []),
  ].map((x) => (x.count === undefined ? { id: x.id, label: x.label } : x));

  const linkedFound = linked !== undefined && list !== undefined && (list.jobs.some((j) => j.id === linked) || list.received.some((r) => r.id === linked));
  const days = list?.limits.ttlDays ?? 7;
  const hours = list?.limits.personalTtlHours ?? 48;

  return (
    <>
      <PageHeader
        title={t('bi.exp.title')}
        subtitle={t('bi.exp.subtitle')}
        actions={
          tab === 'schedules' && canExport ? (
            <Button variant="primary" icon="history" onClick={() => setScheduling({})}>
              {t('bi.exp.sch.new')}
            </Button>
          ) : undefined
        }
      />
      <Stack gap={3}>
        <Notice tone="neutral">{t('bi.exp.where', { days, hours })}</Notice>
        {list !== undefined && (
          <Text role="caption" tone="secondary">{t('bi.exp.limit', { max: list.limits.maxOpen, rows: rowsFmt.format(list.limits.rowCap), size: formatBytes(list.limits.maxBytes) })}</Text>
        )}
        {linked !== undefined && linkedFound && <Notice tone="success">{t('bi.exp.linked')}</Notice>}
        {linked !== undefined && list !== undefined && !linkedFound && <Notice tone="warning">{t('bi.exp.linkedMissing')}</Notice>}
        {problem !== undefined && <Notice tone="warning">{problem}</Notice>}
        {jobs.state.status === 'error' && <Notice tone="warning">{t(jobs.state.error.messageId)}</Notice>}
      </Stack>

      <Tabs id="bi-exports" ariaLabel={t('bi.exp.tabs')} tabs={tabs} value={tab} onChange={setPicked} />

      <TabPanel id="bi-exports" tab="mine" value={tab}>
        <ListTable
          columns={jobColumns}
          rows={list?.jobs ?? []}
          rowKey={(j) => j.id}
          loading={jobs.state.status === 'loading'}
          loadingLabel={t('ui.loading')}
          actions={(j) => (
            <>
              {j.downloadable && (
                <Button size="sm" variant="secondary" icon="download" loading={downloading === j.id} onClick={() => void download(j.id)}>
                  {t('bi.exp.download')}
                </Button>
              )}
              {(isOpenJob(j) || j.status !== 'queued') && canExport && (
                <Button size="sm" variant="ghost" onClick={() => void askRemove(j)}>
                  {isOpenJob(j) ? t('bi.exp.cancelJob') : t('bi.exp.deleteJob')}
                </Button>
              )}
            </>
          )}
          actionsWidth="max-content"
          empty={jobs.state.status === 'error' ? undefined : <EmptyState title={t('bi.exp.empty')} detail={t('bi.exp.empty.hint')} />}
          minWidth={720}
        />
      </TabPanel>

      <TabPanel id="bi-exports" tab="received" value={tab}>
        <ListTable
          columns={receivedColumns}
          rows={list?.received ?? []}
          rowKey={(r) => r.id}
          loading={jobs.state.status === 'loading'}
          loadingLabel={t('ui.loading')}
          actions={(r) => (
            <Button size="sm" variant="secondary" icon="download" loading={downloading === r.id} onClick={() => void download(r.id)}>
              {t('bi.exp.download')}
            </Button>
          )}
          actionsWidth="max-content"
          empty={<EmptyState title={t('bi.exp.received.empty')} detail={t('bi.exp.received.hint')} />}
          minWidth={640}
        />
      </TabPanel>

      {canExport && (
        <>
          <TabPanel id="bi-exports" tab="schedules" value={tab}>
            <ListTable
              columns={scheduleColumns}
              rows={schedules.state.status === 'ready' ? schedules.state.value : []}
              rowKey={(s) => s.id}
              loading={schedules.state.status === 'loading'}
              loadingLabel={t('ui.loading')}
              actions={(s) => (
                <>
                  <Button size="sm" variant="ghost" icon="pencil" onClick={() => setScheduling({ schedule: s })}>
                    {t('bi.exp.sch.edit')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void togglePause(s)}>
                    {s.enabled ? t('bi.exp.sch.pause') : t('bi.exp.sch.resume')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void askDeleteSchedule(s)}>
                    {t('bi.exp.sch.delete')}
                  </Button>
                </>
              )}
              actionsWidth="max-content"
              empty={schedules.state.status === 'error' ? undefined : <EmptyState title={t('bi.exp.sch.empty')} detail={t('bi.exp.sch.empty.hint')} />}
              minWidth={820}
            />
          </TabPanel>
          <TabPanel id="bi-exports" tab="templates" value={tab}>
            <ListTable
              columns={templateColumns}
              rows={templates.state.status === 'ready' ? templates.state.value : []}
              rowKey={(x) => x.id}
              loading={templates.state.status === 'loading'}
              loadingLabel={t('ui.loading')}
              actions={(x) => (
                <>
                  <Button size="sm" variant="secondary" onClick={() => setExporting({ templateId: x.id, title: x.name })}>
                    {t('bi.exp.tpl.use')}
                  </Button>
                  {x.mine && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => void toggleShare(x)}>
                        {x.shared ? t('bi.exp.tpl.shareOff') : t('bi.exp.tpl.shareOn')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void askDeleteTemplate(x)}>
                        {t('bi.exp.tpl.delete')}
                      </Button>
                    </>
                  )}
                </>
              )}
              actionsWidth="max-content"
              empty={templates.state.status === 'error' ? undefined : <EmptyState title={t('bi.exp.tpl.empty')} detail={t('bi.exp.tpl.empty.hint')} />}
              minWidth={720}
            />
          </TabPanel>
        </>
      )}

      <ExportDialog open={exporting !== undefined} target={exporting} onClose={() => { setExporting(undefined); jobs.reload(); }} />
      {scheduling !== undefined && (
        <ScheduleDialog
          open
          {...(scheduling.schedule !== undefined ? { schedule: scheduling.schedule } : {})}
          onClose={() => setScheduling(undefined)}
          onSaved={() => {
            setScheduling(undefined);
            schedules.reload();
          }}
        />
      )}
    </>
  );
}
